'use client';

import { useMemo, useState, type FormEvent } from 'react';
import { Badge } from '@core/ui/primitives/badge';
import { Button } from '@core/ui/primitives/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@core/ui/primitives/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@core/ui/primitives/dropdown-menu';
import { Input } from '@core/ui/primitives/input';
import { Spinner } from '@core/ui/primitives/spinner';
import { useToast } from '@/core/ui/hooks/use-toast';
import { LuChevronDown, LuTrash2, LuCheck, LuX } from 'react-icons/lu';
import {
  useOrganizationAccessManagement,
  type RoleValue,
} from '@vaults/ui/management/UseOrganizationAccessManagementHook';
import { RolePrivilegesInfo } from './RolePrivilegesInfo';

const roleOptions: Array<{ value: RoleValue; label: string }> = [
  { value: 'viewer', label: 'Viewer' },
  { value: 'contributor', label: 'Contributor' },
  { value: 'editor', label: 'Editor' },
  { value: 'manager', label: 'Manager' },
  { value: 'admin', label: 'Admin' },
  { value: 'owner', label: 'Owner' },
];

interface OrganizationMembersPanelProps {
  organizationId: string | null;
  onReady?: (opts?: { immediate?: boolean }) => void;
}

export function OrganizationMembersPanel({ organizationId, onReady }: OrganizationMembersPanelProps) {
  const { toast } = useToast();
  const {
    loading,
    error,
    members,
    memberCount,
    addMember,
    updateMemberRole,
    removeMember,
    allowlist,
    addAllowlistDomain,
    removeAllowlistEntry,
  } = useOrganizationAccessManagement({ organizationId });

  const [inviteForm, setInviteForm] = useState<{ email: string; role: RoleValue }>({
    email: '',
    role: 'contributor',
  });
  const [domainInput, setDomainInput] = useState('');
  const [pendingEntryId, setPendingEntryId] = useState<string | null>(null);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [addingDomain, setAddingDomain] = useState(false);
  const [pendingDomainId, setPendingDomainId] = useState<string | null>(null);

  // Signal readiness when loading finishes
  useMemo(() => {
    if (!loading) {
      onReady?.({ immediate: false });
    }
  }, [loading, onReady]);

  const sortedMembers = useMemo(() => {
    return [...members].sort((a, b) => a.email.localeCompare(b.email));
  }, [members]);

  const handleInvite = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const email = inviteForm.email.trim();
    if (!email) {
      toast({ variant: 'destructive', title: 'Error', description: 'Email is required.' });
      return;
    }
    setCreatingInvite(true);
    try {
      await addMember({ email, role: inviteForm.role });
      setInviteForm({ email: '', role: 'contributor' });
      toast({ variant: 'success', title: 'Success', description: `Added ${email} to the organization.` });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Unable to add member right now.',
      });
    } finally {
      setCreatingInvite(false);
    }
  };

  const handleRoleChange = async (entryId: string, role: RoleValue) => {
    setPendingEntryId(entryId);
    try {
      await updateMemberRole({ entryId, role });
      toast({ variant: 'success', title: 'Success', description: 'Role updated.' });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Unable to update role.',
      });
    } finally {
      setPendingEntryId(null);
    }
  };

  const handleRemoveEntry = async (entryId: string) => {
    setPendingEntryId(entryId);
    try {
      await removeMember(entryId);
      toast({ variant: 'success', title: 'Success', description: 'Member removed.' });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Unable to remove member.',
      });
    } finally {
      setPendingEntryId(null);
      setConfirmRemoveId(null);
    }
  };

  const handleAddDomain = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalized = domainInput.trim().replace(/^@+/, '').toLowerCase();
    if (!normalized) {
      toast({ variant: 'destructive', title: 'Error', description: 'Domain is required.' });
      return;
    }
    if (!/^[a-z0-9.-]+\.[a-z]{2,}$/.test(normalized)) {
      toast({ variant: 'destructive', title: 'Error', description: 'Enter a valid domain.' });
      return;
    }
    setAddingDomain(true);
    try {
      await addAllowlistDomain(normalized);
      setDomainInput('');
      toast({
        variant: 'success',
        title: 'Success',
        description: `Added ${normalized} to the organization allowlist.`,
      });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Unable to add domain.',
      });
    } finally {
      setAddingDomain(false);
    }
  };

  const handleRemoveDomain = async (entryId: string) => {
    setPendingDomainId(entryId);
    try {
      await removeAllowlistEntry(entryId);
      toast({ variant: 'success', title: 'Success', description: 'Domain removed.' });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Unable to remove domain.',
      });
    } finally {
      setPendingDomainId(null);
    }
  };

  if (!organizationId) {
    return (
      <section className='rounded-xl border border-dashed bg-card p-6 text-sm text-muted-foreground'>
        Select an organization to manage members and domain allowlist.
      </section>
    );
  }

  if (loading && members.length === 0) {
    return (
      <div className='flex h-full items-center justify-center'>
        <Badge variant='outline' className='gap-2'>
          <Spinner />
          Loading members
        </Badge>
      </div>
    );
  }

  return (
    <div className='flex h-full flex-col'>
      <div className='flex-1 space-y-4 overflow-y-auto my-1 scrollbar-fade'>
        {/* Members Section */}
        <Card>
          <CardHeader className='space-y-3'>
            <div className='flex flex-row flex-wrap items-center justify-between gap-3'>
              <div className='flex flex-col gap-1'>
                <CardTitle className='text-sm'>Organization members</CardTitle>
                <CardDescription className='text-xs'>
                  Members added here have access to all vaults in the organization based on their role.
                </CardDescription>
              </div>
              <form onSubmit={handleInvite} className='flex flex-wrap items-center gap-2'>
                <Input
                  type='email'
                  placeholder='name@example.com'
                  value={inviteForm.email}
                  onChange={(event) => setInviteForm((prev) => ({ ...prev, email: event.target.value }))}
                  className='w-48'
                  required
                />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant='outline'>
                      {roleOptions.find((o) => o.value === inviteForm.role)?.label ?? 'Select role'}
                      <LuChevronDown className='h-3 w-3 opacity-50' />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align='end'>
                    <DropdownMenuRadioGroup
                      value={inviteForm.role}
                      onValueChange={(value) => setInviteForm((prev) => ({ ...prev, role: value as RoleValue }))}>
                      {roleOptions
                        .filter((option) => option.value !== 'owner')
                        .map((option) => (
                          <DropdownMenuRadioItem key={option.value} value={option.value}>
                            {option.label}
                          </DropdownMenuRadioItem>
                        ))}
                    </DropdownMenuRadioGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button type='submit' disabled={creatingInvite}>
                  {creatingInvite ? 'Adding…' : 'Add member'}
                </Button>
              </form>
            </div>
            <RolePrivilegesInfo role={inviteForm.role} className='w-full rounded-md border border-dashed p-2' />
          </CardHeader>
          <CardContent>
            {error && !loading ? (
              <p className='text-sm text-rose-600'>{error}</p>
            ) : sortedMembers.length === 0 ? (
              <p className='text-sm text-muted-foreground'>
                No organization-level members yet. Use the form above to add members.
              </p>
            ) : (
              <div className='space-y-2'>
                {sortedMembers.map((entry) => {
                  const isOwner = entry.role === 'owner';
                  return (
                    <div
                      key={entry.id}
                      className='flex items-center justify-between gap-3 rounded-lg border border-dashed px-3 py-2'>
                      <div className='flex min-w-0 flex-1 flex-col'>
                        <span className='truncate text-sm'>{entry.email}</span>
                        {entry.user?.name && (
                          <span className='truncate text-xs text-muted-foreground'>{entry.user.name}</span>
                        )}
                      </div>
                      <div className='flex items-center gap-2'>
                        {isOwner ? (
                          <Badge variant='outline' className='font-normal'>
                            Owner
                          </Badge>
                        ) : (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant='outline'
                                className='h-9'
                                disabled={pendingEntryId === entry.id}>
                                {roleOptions.find((o) => o.value === entry.role)?.label ?? entry.role}
                                <LuChevronDown className='h-3 w-3 opacity-50' />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align='end'>
                              <DropdownMenuRadioGroup
                                value={entry.role}
                                onValueChange={(value) => handleRoleChange(entry.id, value as RoleValue)}>
                                {roleOptions
                                  .filter((option) => option.value !== 'owner')
                                  .map((option) => (
                                    <DropdownMenuRadioItem key={option.value} value={option.value}>
                                      {option.label}
                                    </DropdownMenuRadioItem>
                                  ))}
                              </DropdownMenuRadioGroup>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                        {!isOwner && (
                          <div className='flex h-8 w-8 items-center justify-center'>
                            {confirmRemoveId === entry.id ? (
                              <div className='flex items-center gap-1'>
                                <button
                                  type='button'
                                  onClick={() => handleRemoveEntry(entry.id)}
                                  disabled={pendingEntryId === entry.id}
                                  className='flex h-6 w-6 items-center justify-center rounded text-brand hover:bg-brand/10 disabled:opacity-50'
                                  title='Confirm remove'
                                  aria-label='Confirm remove'>
                                  <LuCheck className='h-3.5 w-3.5' />
                                </button>
                                <button
                                  type='button'
                                  onClick={() => setConfirmRemoveId(null)}
                                  className='flex h-6 w-6 items-center justify-center rounded text-destructive hover:bg-destructive/10'
                                  title='Cancel'
                                  aria-label='Cancel'>
                                  <LuX className='h-3.5 w-3.5' />
                                </button>
                              </div>
                            ) : (
                              <button
                                type='button'
                                onClick={() => setConfirmRemoveId(entry.id)}
                                disabled={pendingEntryId === entry.id}
                                className='flex h-8 w-8 items-center justify-center rounded text-muted-foreground hover:text-destructive disabled:opacity-50'>
                                <LuTrash2 className='h-4 w-4' />
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Domain Allowlist Section */}
        <Card>
          <CardHeader className='flex-row flex-wrap items-center justify-between gap-3'>
            <div className='flex flex-col gap-1'>
              <CardTitle className='text-sm'>Organization domain allowlist</CardTitle>
              <CardDescription className='text-xs'>
                Domains added here apply to all vaults in the organization.
              </CardDescription>
            </div>
            <form onSubmit={handleAddDomain} className='flex flex-wrap items-center gap-2'>
              <Input
                type='text'
                placeholder='example.com'
                value={domainInput}
                onChange={(event) => setDomainInput(event.target.value)}
                className='w-48'
              />
              <Button type='submit' disabled={addingDomain}>
                {addingDomain ? 'Adding…' : 'Add domain'}
              </Button>
            </form>
          </CardHeader>
          <CardContent>
            {allowlist.length === 0 ? (
              <p className='text-sm text-muted-foreground'>
                No organization domains yet. Add one above to allow access from a specific domain.
              </p>
            ) : (
              <ul className='space-y-2 text-sm'>
                {allowlist.map((entry) => {
                  const display = entry.value.startsWith('@') ? entry.value : `@${entry.value}`;
                  return (
                    <li
                      key={entry.id}
                      className='flex items-center justify-between rounded-lg border border-dashed px-3 py-2'>
                      <span>{display}</span>
                      <Button
                        size='icon'
                        variant='ghost'
                        className='hover:text-destructive'
                        disabled={pendingDomainId === entry.id}
                        onClick={() => handleRemoveDomain(entry.id)}>
                        <LuTrash2 className='h-4 w-4' />
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
